import "./site-scale.css";

/**
 * Laptop scale for the public site (#2817).
 *
 * The public pages were drawn for a 1920px-wide window. A laptop with Windows
 * display scaling at 125% to 150% gives the page a CSS viewport of 1280x720 to
 * 1536x864, so every px size reads 1.25x to 1.5x too large. We shrink each
 * public page root with CSS `zoom` by `width / 1920`, so a 1280px window shows
 * the 1920 layout at two thirds of its size, the way browser zoom at 67% would.
 *
 * - Below 1024px (phones, tablets) and on a touch-first screen: no zoom.
 * - 1920px and wider: no zoom.
 * - Never below 2/3: text stays readable in a narrow desktop window.
 *
 * `zoom` also scales every viewport unit used inside the root, so a stage at
 * `100vh` would end at two thirds of the screen. The PostCSS step in
 * `web/postcss-la-viewport.js` rewrites the vh and vw units of the public CSS to
 * `--la-vh` / `--la-vw`, and `site-scale.css` sets those from the values here:
 * one viewport unit in the root's own (unzoomed) px, so it renders true.
 */

export const SITE_DESIGN_WIDTH = 1920;
export const SITE_MIN_WIDTH = 1024;
export const SITE_MIN_ZOOM = 2 / 3;

/** The zoom for a window `width` CSS px wide; 1 means none. */
export function siteZoom(width: number, coarse = false): number {
  if (coarse || width < SITE_MIN_WIDTH) return 1;
  return Math.min(1, Math.max(SITE_MIN_ZOOM, width / SITE_DESIGN_WIDTH));
}

/** The zoom the page `el` sits under (1 outside a scaled root). Rects and
 *  pointer coordinates are in screen px; `offset*`, `clientHeight` and px
 *  written to `style` are in the root's own px. Divide a screen length by this
 *  to get a root length. */
export function cssZoomOf(el: Element | null | undefined): number {
  const z = (el as (Element & { currentCSSZoom?: number }) | null | undefined)?.currentCSSZoom;
  return typeof z === "number" && z > 0 ? z : 1;
}

/** True where the browser applies standard CSS `zoom` and reports it through
 *  `currentCSSZoom` (Chrome 128+, Firefox 126+). Elsewhere a half-applied zoom
 *  (units corrected, page not shrunk, or rects not converted) reads worse than
 *  none, so those browsers get the page as drawn. */
export function siteZoomSupported(): boolean {
  return typeof Element !== "undefined" && "currentCSSZoom" in Element.prototype;
}

/** Writes the zoom and the corrected viewport units for the current window. */
export function publishSiteScale(): void {
  const root = document.documentElement;
  const coarse = window.matchMedia?.("(pointer: coarse)").matches ?? false;
  const z = siteZoomSupported() ? siteZoom(window.innerWidth, coarse) : 1;
  if (z === 1) {
    root.style.removeProperty("--la-site-z");
    root.style.removeProperty("--la-site-vw");
    root.style.removeProperty("--la-site-vh");
    return;
  }
  root.style.setProperty("--la-site-z", z.toFixed(4));
  root.style.setProperty("--la-site-vw", `${(window.innerWidth / 100 / z).toFixed(3)}px`);
  root.style.setProperty("--la-site-vh", `${(window.innerHeight / 100 / z).toFixed(3)}px`);
}

/** Sets the scale before the first render and keeps it on a resize or a
 *  switch between mouse and touch (a 2-in-1 going into tablet mode). */
export function installSiteScale(): void {
  if (typeof window === "undefined") return;
  publishSiteScale();
  window.addEventListener("resize", publishSiteScale);
  window.matchMedia?.("(pointer: coarse)").addEventListener?.("change", publishSiteScale);
}
